const ONE_MINUTE = 60 * 1000;
const INACTIVITY_TIMEOUT_MINUTES = 15;

/**
 * Creates an inactivity tracker.
 * Returns methods + state (via getters), so the component can read pauseCausedByInactivity etc.
 */
export default function createInactivityTracking({
  isTrainingVideoPlaying = () => false,
  isSessionTimerPaused = () => false,
  getSessionTimerTime = () => null,
  onPauseSessionTimer = async () => {},
  intervalMs = ONE_MINUTE,
  inactivityTimeoutMinutes = INACTIVITY_TIMEOUT_MINUTES,
} = {}) {
  let userWasActiveInInterval = false;
  let inactivityCheckIntervalId = null;
  let inactiveMinutes = 0;
  let inactivityTrackingCreated = false;
  let pauseCausedByInactivity = false;

  const scrollOptions = { passive: true };

  const setUserActive = () => {
    userWasActiveInInterval = true;
  };

  const pauseSessionOnVisibilityChange = async () => {
    if (isSessionTimerPaused() || document?.visibilityState !== 'hidden') {
      return;
    }

    pauseCausedByInactivity = true;

    const sessionTimerTime = getSessionTimerTime();
    if (sessionTimerTime != null) {
      await onPauseSessionTimer(sessionTimerTime);
    }
  };

  const checkInactivity = async () => {
    if (isTrainingVideoPlaying()) {
      userWasActiveInInterval = false;
      inactiveMinutes = 0;
      return;
    }

    if (userWasActiveInInterval) {
      inactiveMinutes = 0;
      // clear flag for the next interval
      userWasActiveInInterval = false;
    } else {
      inactiveMinutes += 1;
    }

    if (inactiveMinutes === inactivityTimeoutMinutes) {
      inactiveMinutes = 0;
      pauseCausedByInactivity = true;

      const sessionTimerTime = getSessionTimerTime();
      if (sessionTimerTime != null) {
        // Shows a dialog and stops inactivity tracking
        // only restarts it after the user clicks “Ok”,
        // so this block cannot be hit repeatedly while the dialog is open
        await onPauseSessionTimer(sessionTimerTime);
      }
    }
  };

  const setupInactivityTracking = () => {
    if (inactivityTrackingCreated) {
      return;
    }

    userWasActiveInInterval = false;
    pauseCausedByInactivity = false;
    inactiveMinutes = 0;

    window.addEventListener('mousemove', setUserActive);
    window.addEventListener('mousedown', setUserActive);
    window.addEventListener('click', setUserActive);
    window.addEventListener('keydown', setUserActive);
    window.addEventListener('touchstart', setUserActive);
    window.addEventListener('scroll', setUserActive, scrollOptions);

    // Shows a dialog and stops timer when user navigates away
    window.addEventListener('visibilitychange', pauseSessionOnVisibilityChange);

    inactivityCheckIntervalId = setInterval(async () => {
      try {
        await checkInactivity();
      } catch (e) {
        console.error(e);
      }
    }, intervalMs);

    inactivityTrackingCreated = true;
  };

  const removeInactivityTracking = () => {
    if (!inactivityTrackingCreated) {
      return;
    }

    if (inactivityCheckIntervalId) {
      clearInterval(inactivityCheckIntervalId);
      inactivityCheckIntervalId = null;
    }

    window.removeEventListener('mousemove', setUserActive);
    window.removeEventListener('mousedown', setUserActive);
    window.removeEventListener('click', setUserActive);
    window.removeEventListener('keydown', setUserActive);
    window.removeEventListener('touchstart', setUserActive);
    window.removeEventListener('scroll', setUserActive, scrollOptions);

    window.removeEventListener('visibilitychange', pauseSessionOnVisibilityChange);

    inactivityTrackingCreated = false;
  };

  return {
    setupInactivityTracking,
    removeInactivityTracking,

    get pauseCausedByInactivity() {
      return pauseCausedByInactivity;
    },
  };
}
